/**
 * Name: Edit Playlist
 * Author: @Gaurav
 * Date: 20-07-2021
 */
import React, { useState } from 'react';
import Sidebar from '../../components/SideBar';
import NavBar from '../../components/NavBarSearchSong';
import Settings from '../../assets/Settings.png';
import search from '../../assets/ic_search.png';
import RoundButton from '../../components/RoundButton';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import PlayBtn from '../../assets/play_btn.svg';
import AlertModal from '../../components/Modal';
import SortIndicator from '../../components/SortIndicator';
import PlaylistActionIcon from '../../components/PlaylistManagement/PlaylistActionIcon';
import RangeSelectionButton from '../../components/RangeSelectionButton';
import useRangeSelection, { selectionShouldCheck } from '../../utils/useRangeSelection';

/* 
 Table Data and Use Style
 */
const useStyles = makeStyles({
  table: {
    minWidth: 372,
    borderRadius: 30,
  },
});

function EditPlaylist({
  isRoyalties,
  playlistToUpdate,
  onPlaylistTabClick,
  onUpdatePlaylist,
  selectedSongsToUpload,
  selectedCurrentSongIds,
  handleSelectCurrentSongs,
  handleRemoveSelectedSongs,
  handleSelectSongsToUpload,
  handleMultipleSongsToUpload,
  handleRemoveSong,
  handleSearchInput,
  searchInput,
  allPlaylist,
  handleSelectPlaylist,
  selectedPlaylist,
  handlePlaySong,
  isSongLoading,
  isSongPlaying,
  currentPlayingSong,
  isPageOpenedAsPopup,
}) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [deleteSelectedPlaylist, setDeleteSelectedPlaylist] = useState(null);
  const [navbarCurrentSong, setNavbarCurrentSong] = useState(null);
  const { choose: chooseRange, toggleRangeMode, resetRange, isRangeMode } = useRangeSelection();

  // Sorting states
  const [playlistSortConfig, setPlaylistSortConfig] = useState({ key: null, direction: 'desc' });
  const [songsSortConfig, setSongsSortConfig] = useState({ key: null, direction: 'desc' });
  const [selectedSortConfig, setSelectedSortConfig] = useState({ key: null, direction: 'desc' });

  /**
   * Close Modal
   */
  const handleClose = () => {
    setOpen(false);
  };

  const filterAllOtherThanSelected = (playList) => {
    if (Array.isArray(playList) && playList.length) {
      const typeToFilter = isRoyalties ? 3 : 4;
      return playList.filter((item) => item.id !== playlistToUpdate.id && item.type === typeToFilter);
    }
    return [];
  };

  const getPlaylistSongsList = () => {
    if (Array.isArray(selectedPlaylist)) {
      let songsList = [];
      selectedPlaylist.forEach((item) => {
        songsList = [...songsList, ...item.songs];
      });
      return songsList;
    }
    return [];
  };

  const isAllSongSelected = () => {
    const currentPlaylistSongs = getPlaylistSongsList();

    if (!currentPlaylistSongs.length) return false;

    // check if each song is selected
    return currentPlaylistSongs.every((song) => selectedSongsToUpload.some((selectedSong) => selectedSong.id === song.id));
  };

  const handleRemoveSongLocal = (deleteSelectedPlaylist) => {
    if (Array.isArray(deleteSelectedPlaylist)) handleRemoveSelectedSongs();
    else handleRemoveSong(deleteSelectedPlaylist);
    setOpen(false);
  };

  const handleSort = (key, sortConfig, setSortConfig) => {
    let direction = 'desc';
    let newKey = key;

    if (sortConfig.key === key) {
      if (sortConfig.direction === 'desc') {
        direction = 'asc';
      } else {
        newKey = null;
      }
    }

    setSortConfig({ key: newKey, direction });
  };

  const sortData = (data, sortConfig, defaultData = []) => {
    if (!sortConfig.key || !Array.isArray(data)) return data;

    return [...data].sort((a, b) => {
      const key = sortConfig.key;
      const valueA = (a[key] || a.fileName || a.name || a.title || '').toString();
      const valueB = (b[key] || b.fileName || b.name || b.title || '').toString();

      return sortConfig.direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  };

  // Get current songs list for navigation
  const currentSongsList = getPlaylistSongsList();

  // Find current song index in the list
  const getCurrentSongIndex = () => {
    if (!navbarCurrentSong) return -1;
    return currentSongsList.findIndex(song => song.id === navbarCurrentSong.id);
  };

  // Check if there's a next song
  const hasNextSong = () => {
    const currentIndex = getCurrentSongIndex();
    return currentIndex >= 0 && currentIndex < currentSongsList.length - 1;
  };

  // Check if there's a previous song
  const hasPrevSong = () => {
    const currentIndex = getCurrentSongIndex();
    return currentIndex > 0;
  };

  // Handle playing next song
  const handleNextSong = () => {
    const currentIndex = getCurrentSongIndex();
    if (currentIndex >= 0 && currentIndex < currentSongsList.length - 1) {
      const nextSong = currentSongsList[currentIndex + 1];
      setNavbarCurrentSong(nextSong);
    }
  };

  // Handle playing previous song
  const handlePrevSong = () => {
    const currentIndex = getCurrentSongIndex();
    if (currentIndex > 0) {
      const prevSong = currentSongsList[currentIndex - 1];
      setNavbarCurrentSong(prevSong);
    }
  };

  // Handle playing song from row (updates navbar player only)
  const handlePlaySongFromRow = (song) => {
    setNavbarCurrentSong(song);
  };

  // Handle closing the player
  const handleClosePlayer = () => {
    setNavbarCurrentSong(null);
  };

  const isNavbarSongAddedToSelected =
    !!navbarCurrentSong && selectedSongsToUpload.some((item) => item.id === navbarCurrentSong.id);

  const handleToggleNavbarSongInSelected = (song) => {
    if (!song?.id) return;
    const currentlyAdded = selectedSongsToUpload.some((item) => item.id === song.id);
    handleSelectSongsToUpload({ target: { checked: !currentlyAdded } }, song);
  };

  return (
    <div className={`main ${isPageOpenedAsPopup ? 'edit-playlist-popup' : ''}`}>
      {!isPageOpenedAsPopup && (
        <div className="sidebar">
          <Sidebar />
        </div>
      )}
      <AlertModal
        open={open}
        onClose={handleClose}
        handelDelete={() => handleRemoveSongLocal(deleteSelectedPlaylist)}
        description={Array.isArray(deleteSelectedPlaylist)
          ? `Remove ${deleteSelectedPlaylist.length} selected songs from this playlist? Save to apply the change.`
          : 'Remove this song from the playlist? Save to apply the change.'}
      />
      <div className="leftPortion">
        <NavBar
          data="Edit Playlist"
          icon={Settings}
          currentRouteName="Playlist"
          breadcrumbsPath="Edit Playlist / Playlist"
          showPlayer={true}
          currentSong={navbarCurrentSong}
          onNextSong={handleNextSong}
          hasNextSong={hasNextSong()}
          onPrevSong={handlePrevSong}
          hasPrevSong={hasPrevSong()}
          onClosePlayer={handleClosePlayer}
          showAddToPlaylist={true}
          isCurrentSongAddedToPlaylist={isNavbarSongAddedToSelected}
          onToggleAddToPlaylist={handleToggleNavbarSongInSelected}
        />
        <div className="content">
          <div className="warp_edit_row">
            <div className="editRow">
              <div className="wrap_edit">
                <p>Edit:</p>
                <RoundButton btnText="Playlist" />
                <RoundButton className="disabled-button" handleClick={() => onPlaylistTabClick('playlistServer')} btnText="server" />
              </div>

              <div className="search_row wdth">
                <input
                  className="input_field"
                  placeholder="Search something here..."
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchInput(e.target.value)}
                />
                <img src={search} alt="search" />
              </div>
            </div>
            <div className="">
              <RoundButton
                btnText={`Remove selected (${selectedCurrentSongIds.length})`}
                disabled={!selectedCurrentSongIds.length}
                handleClick={() => { setDeleteSelectedPlaylist(selectedSongsToUpload.filter((song) => selectedCurrentSongIds.includes(song.id))); setOpen(true); }}
              />
              <RoundButton btnText="Save" handleClick={onUpdatePlaylist} />
            </div>
          </div>

          <div className="main_tbl_wrap">
            <TableContainer
              className="tbl_wraper tbl_wrap1"
              style={{ border: '1px solid #40189D', borderRight: 'none', borderRadius: 0 }}
              component={Paper}
            >
              <Table style={{ borderTopRightRadius: 0 }} className={classes.table} stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ position: 'sticky', left: 0, zIndex: 4, backgroundColor: '#fafafa', width: '5%' }}>
                      <Checkbox
                        checked={filterAllOtherThanSelected(allPlaylist).length > 0 && filterAllOtherThanSelected(allPlaylist).every((playlist) => selectedPlaylist.some((item) => item.id === playlist.id))}
                        indeterminate={selectedPlaylist.length > 0 && !filterAllOtherThanSelected(allPlaylist).every((playlist) => selectedPlaylist.some((item) => item.id === playlist.id))}
                        onChange={(event) => {
                          resetRange('source-playlists');
                          filterAllOtherThanSelected(allPlaylist).forEach((playlist) => handleSelectPlaylist(event, playlist));
                        }}
                        inputProps={{ 'aria-label': 'Select all source playlists' }}
                      />
                      <RangeSelectionButton active={isRangeMode('source-playlists')} onClick={() => toggleRangeMode('source-playlists')} />
                    </TableCell>
                    <TableCell className="table_font">Result</TableCell>
                    <TableCell 
                      onClick={() => handleSort('name', playlistSortConfig, setPlaylistSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Playlist Name<SortIndicator sortConfig={playlistSortConfig} columnKey="name" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortData(filterAllOtherThanSelected(allPlaylist), playlistSortConfig).map((row, index) => (
                    <TableRow key={index}>
                      <TableCell style={{ position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#ffffff', width: '5%' }}>
                        <Checkbox
                          checked={selectedPlaylist.some((item) => item.id === row.id) || false}
                          onChange={(event) => {
                            const selection = chooseRange('source-playlists', sortData(filterAllOtherThanSelected(allPlaylist), playlistSortConfig), row, event.target.checked, !!event.nativeEvent?.shiftKey);
                            const checked = selectionShouldCheck(selectedPlaylist.map((item) => item.id), selection);
                            selection.items.forEach((item) => handleSelectPlaylist({ target: { checked } }, item));
                          }}
                          inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                        />
                      </TableCell>
                      <TableCell className="table-index-cell">#{++index}</TableCell>
                      <TableCell className="table-name-cell">{row.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* <Box display="flex" height="100%" alignItems="center">
              <div className="divider-line" />
            </Box> */}
            <TableContainer className="tbl_wraper tbl_wrap2" style={{ border: '1px solid #40189D' }} component={Paper}>
              <Table className={classes.table} stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#FAFAFA' }}>
                      <Checkbox
                        inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                        onChange={(event) => {
                          resetRange('source-songs');
                          handleMultipleSongsToUpload(event, getPlaylistSongsList());
                        }}
                        checked={isAllSongSelected()}
                      />
                      <RangeSelectionButton active={isRangeMode('source-songs')} onClick={() => toggleRangeMode('source-songs')} />
                    </TableCell>
                    <TableCell className="table_font">Result</TableCell>
                    <TableCell 
                      onClick={() => handleSort('name', songsSortConfig, setSongsSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Song Name<SortIndicator sortConfig={songsSortConfig} columnKey="name" />
                    </TableCell>
                    <TableCell 
                      onClick={() => handleSort('artist', songsSortConfig, setSongsSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Artist<SortIndicator sortConfig={songsSortConfig} columnKey="artist" />
                    </TableCell>
                    <TableCell style={{ position: 'sticky', right: 0, zIndex: 3, backgroundColor: '#FAFAFA' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortData(getPlaylistSongsList(), songsSortConfig)?.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell style={{ position: 'sticky', left: 0, zIndex: 0, backgroundColor: '#fff' }}>
                        <Checkbox
                          onChange={(event) => {
                            const selection = chooseRange('source-songs', sortData(getPlaylistSongsList(), songsSortConfig), row, event.target.checked, !!event.nativeEvent?.shiftKey);
                            handleMultipleSongsToUpload({ target: { checked: selectionShouldCheck(selectedSongsToUpload.map((item) => item.id), selection) } }, selection.items);
                          }}
                          inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                          checked={selectedSongsToUpload.some((item) => item.id === row.id) || false}
                          name="song"
                        />
                      </TableCell>
                      <TableCell className="table-index-cell">#{++index}</TableCell>
                      <TableCell className="table-name-cell">{row.fileName || row.name || 'N/A'}</TableCell>
                      <TableCell className="table-name-cell">{row.artist || 'N/A'}</TableCell>
                      <TableCell style={{ position: 'sticky', right: 0, zIndex: 1, backgroundColor: '#fff' }}>
                        <img
                          src={PlayBtn}
                          alt="play"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handlePlaySongFromRow(row)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer className="tbl_wraper  selectTbl" component={Paper}>
              <Table className={classes.table} stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={selectedSongsToUpload.length > 0 && selectedSongsToUpload.every((song) => selectedCurrentSongIds.includes(song.id))}
                        indeterminate={selectedCurrentSongIds.length > 0 && selectedCurrentSongIds.length < selectedSongsToUpload.length}
                        onChange={(event) => {
                          resetRange('selected-songs');
                          handleSelectCurrentSongs(selectedSongsToUpload, event.target.checked);
                        }}
                        inputProps={{ 'aria-label': 'Select all songs in this playlist' }}
                      />
                      <RangeSelectionButton active={isRangeMode('selected-songs')} onClick={() => toggleRangeMode('selected-songs')} />
                    </TableCell>
                    <TableCell className="table_font">Result</TableCell>
                    <TableCell 
                      onClick={() => handleSort('name', selectedSortConfig, setSelectedSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Selected Songs<SortIndicator sortConfig={selectedSortConfig} columnKey="name" />
                    </TableCell>
                    <TableCell 
                      onClick={() => handleSort('artist', selectedSortConfig, setSelectedSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Artist<SortIndicator sortConfig={selectedSortConfig} columnKey="artist" />
                    </TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(selectedSongsToUpload) &&
                    sortData(selectedSongsToUpload, selectedSortConfig).map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Checkbox
                            checked={selectedCurrentSongIds.includes(row.id)}
                            onChange={(event) => {
                              const selection = chooseRange('selected-songs', sortData(selectedSongsToUpload, selectedSortConfig), row, event.target.checked, !!event.nativeEvent?.shiftKey);
                              handleSelectCurrentSongs(selection.items, selectionShouldCheck(selectedCurrentSongIds, selection));
                            }}
                            inputProps={{ 'aria-label': `Select ${row.title || row.name || row.fileName || 'song'}` }}
                          />
                        </TableCell>
                        <TableCell className="table-index-cell">#{++index}</TableCell>
                        <TableCell className="table-name-cell">{row.title || row.fileName || row.name || 'N/A'}</TableCell>
                        <TableCell className="table-name-cell">{row.artist || 'N/A'}</TableCell>
                        <TableCell>
                          <div className="playlist-management-actions">
                            <PlaylistActionIcon kind="listen" label={`Listen to ${row.title || row.name || row.fileName || 'song'}`} onClick={() => handlePlaySongFromRow(row)} />
                            <PlaylistActionIcon kind="remove" label={`Remove ${row.title || row.name || row.fileName || 'song'} from this playlist`} onClick={() => { setOpen(true); setDeleteSelectedPlaylist(row); }} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
export default EditPlaylist;
